import type {
	FetchFunction,
	GraphQLResponse,
	RequestParameters,
	Subscription,
} from 'relay-runtime';
import { Observable } from 'relay-runtime';
import delay from 'lodash/delay';

import { BadStatusError } from '@confluence/network';

import { calculateExponentialBackoff } from './calculateExponentialBackoff';

const BAD_STATUS_CODE_MAX_RETRIES = 2;
const BAD_STATUS_CODE_INITIAL_RETRY_DELAY_MS = 500;
const BAD_STATUS_CODE_MAX_RETRY_DELAY_MS = 5000;

let retryEnabled = false;

const shouldRetryOnBadStatus = (error: Error, request: RequestParameters): boolean => {
	if (!retryEnabled) return false;
	if (error instanceof BadStatusError) {
		if (request.operationKind === 'mutation') return false;
		return true;
	}
	return false;
};

/**
 * Relay counterpart to Apollo's StatusCodeRetryLink.
 * Retries queries (not mutations) on BadStatusError with exponential backoff.
 */
export const createStatusCodeRetryMiddleware = (fetchFn: FetchFunction): FetchFunction => {
	return (request, variables, cacheConfig) => {
		return Observable.create((observer) => {
			let retryCount = 0;
			let currentSubscription: Subscription | null = null;

			const attemptFetch = () => {
				currentSubscription = Observable.from(fetchFn(request, variables, cacheConfig)).subscribe({
					next(result: GraphQLResponse) {
						observer.next(result);
					},

					error(error: Error) {
						// Early return: don't retry if we shouldn't or have exhausted retries
						if (
							!shouldRetryOnBadStatus(error, request) ||
							retryCount >= BAD_STATUS_CODE_MAX_RETRIES
						) {
							observer.error(error);
							return;
						}

						retryCount++;
						const retryDelay = calculateExponentialBackoff(
							retryCount,
							BAD_STATUS_CODE_INITIAL_RETRY_DELAY_MS,
							BAD_STATUS_CODE_MAX_RETRY_DELAY_MS,
						);
						if (process.env.NODE_ENV !== 'testing') {
							delay(() => attemptFetch(), retryDelay);
						} else {
							attemptFetch();
						}
					},
					complete() {
						observer.complete();
					},
				});
			};

			attemptFetch();

			return () => {
				if (currentSubscription) {
					currentSubscription.unsubscribe();
				}
			};
		});
	};
};

export function enableStatusCodeRetryMiddleware() {
	retryEnabled = true;
}

export function disableStatusCodeRetryMiddleware() {
	retryEnabled = false;
}

export function isStatusCodeRetryEnabled(): boolean {
	return retryEnabled;
}
